import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import html2canvas from 'html2canvas'

import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { applyConfig, commonConfig } from '../../configs'
import { checkCorporateRegiNumber, file2DataUrl } from '../../tools'
import { SAMPLE_PASSWORD } from '../../tools/constants'

import createFormSchema from './createFormSchema'

const useApplicationForm = ({ isSpgeF, isSpgeCF, isNormal2Premium } = {}) => {
  const navi = useNavigate()
  const location = useLocation()
  const param = useParams()

  const [notEditInfo, setNotEditInfo] = useState(false)
  const [isSpge2Frch, setIsSpge2Frch] = useState(false)
  const [cfExists, setCfExists] = useState(false)

  const [managerName, setManagerName] = useState() // 메니저 이름
  const [managerIdx, setManagerIdx] = useState() // 메니저 이름
  const [franchiseManagerIdx, setFranchiseManagerIdx] = useState() // 메니저 이름
  const [serviceNum, setServiceNum] = useState(0) // 서비스 체크 수
  const [serviceInfo, setServiceInfo] = useState([]) // 서비스 계정 내용
  const [serviceHelper, setServiceHelper] = useState() // 서비스 계정 선택안했을시
  const [cost, setCost] = useState(0) // 선택한 서비스의 사용료
  const [successPopupState, setSuccessPopupState] = useState(false) // 성공 팝업 상태
  const [errorPopupState, setErrorPopupState] = useState(false) // 실패 팝업 상태
  const [errorPopupMessage, setErrorPopupMessage] = useState() // 실패 팝업 메세지

  const [btnState, setBtnState] = useState(false) // 시작 예상
  const [serviceStartDt, setServiceStartDt] = useState() // 시작 예상
  const [withdrawalDt, setWithdrawalDt] = useState() // 출금 예상

  const [submitCount, setSubmitCount] = useState(0)

  const [shopId, setShopId] = useState('')
  const [shopPassword, setShopPassword] = useState('')
  const [shopConfirmPassword, setShopConfirmPassword] = useState('')
  const [reSignup, setReSignup] = useState(false)
  const [ceoIdx, setCeoIdx] = useState('')

  const [selectList, setSelectList] = useState({
    bank_code: [{ name: '은행 선택', value: '' }]
  })

  const [isTargetManager, setIsTargetManager] = useState(false) // 프로모션 진행 매니저 여부
  const [isServiceManager, setIsServiceManager] = useState(false) // 0원 고정 진행 매니저 여부

  const formRef = useRef()
  const different_customer_sign_ref = useRef(null)
  const different_account_sign_ref = useRef(null)

  const samplePassword = SAMPLE_PASSWORD

  const formSchema = useMemo(
    () =>
      createFormSchema({
        notEditInfo,
        isSpgeCF,
        isSpgeF,
        samplePassword,
        isServiceManager
      }),
    [isSpgeCF, isSpgeF, notEditInfo, samplePassword, isServiceManager]
  )

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(formSchema),

    defaultValues: {
      biz_no_type: 'p',
      biz_no: '',
      shop_name: '',
      owner_name: '',
      owner_phone_no: '',
      account_name: '',
      account_phone_no: '',
      bank_code: '',
      account_no: '',
      account_jumin_biz_no: '',
      different_customer_name: '',
      apply_phone_no: '',
      id: '',
      password: '',
      confirmPassword: ''
    }
  })

  // 매니저코드 확인
  useEffect(() => {
    window.scrollTo(0, 0)

    getBankCode()

    const isDevelop = window.location.hostname === 'localhost' || window.location.hostname === 'dev-r.spge.kr'
    const managerCode = isDevelop ? 'okop71rq' : 'udh7llot'

    if (param.manager_code === managerCode) {
      setIsTargetManager(true)
    }

    if (param.manager_code === 'okop71rq') {
      setIsServiceManager(true)
    }

    if (param.manager_code) {
      getMangerVerf(param, setManagerName)
    }

    if (param.shop_idx) {
      getShopInfo(param.shop_idx)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('mousewheel', function () {
      if (document.activeElement.type === 'number') {
        document.activeElement.blur()
      }
    })
  }, [])

  /**
   * 가맹점 정보 조회
   * @param {string} shopIdx
   */
  const getShopInfo = async shopIdx => {
    try {
      const res = await applyConfig.getShopInfo(shopIdx)

      if (res) {
        setBizNoTrue(true)
        setValueForExistedShop(res)
      }
    } catch (error) {}
  }

  /**
   *
   * @param {*} checkbox
   * @param {*} info
   */
  const serviceBoxInputCheckedEvh = (checkbox, info) => {
    checkbox.parentNode.parentNode.classList.add('isOpen')

    setServiceNum(serviceNum + 1)
    setServiceInfo([...serviceInfo, info])
    setServiceHelper('')
  }

  /**
   *
   * @param {ClickEvent} e - event
   * @param {string} id -
   */
  const serviceBoxInputEvh = (e, id) => {
    if (e.target.checked === true) {
      serviceBoxInputCheckedEvh(e.target, { app_type_code: id, app_id: '', app_password: '' })
    } else {
      // 값초기화
      const tempId = document.getElementById(`service_id_${id}`)
      const tempPassword = document.getElementById(`service_password_${id}`)

      if (tempId) tempId.value = ''
      if (tempPassword) tempPassword.value = ''

      // 열기
      e.target.parentNode.parentNode.classList.remove('isOpen')

      // 데이터 삭제
      setServiceNum(serviceNum - 1)
      setServiceInfo(serviceInfo.filter(item => item.app_type_code !== id))
    }
  }

  /**
   *
   * @param {*} data
   */
  const setValueForExistedShop = data => {
    setShopId(data.id)
    setShopPassword(data.password)
    setShopConfirmPassword(data.password)
    setValue('id', data.id)
    setValue('password', data.password)
    setValue('confirmPassword', data.password)
    setValue('biz_no', data.biz_no)
    setValue('shop_name', data.shop_name)
    setValue('owner_name', data.owner_name)
    setValue('owner_phone_no', data.owner_phone_no)
    setValue('different_customer_name', data.owner_name)
    setValue('apply_phone_no', data.owner_phone_no)
    const delivery_apps = data['delivery_apps']
    if (delivery_apps?.length) {
      setTimeout(function () {
        for (const app of delivery_apps) {
          try {
            const { app_type_code: code, app_id } = app
            const box = document.querySelector('#sevice-box-' + code)
            if (!box) continue
            const checkbox = box.querySelector("input[type='checkbox']")
            checkbox.checked = true
            // setValue('service_id_' + code, app_id ?? '')
            // setValue('service_password_' + code, '******')
            box.querySelector('#service_id_' + code).value = app_id
            box.querySelector('#service_password_' + code).value = '******'

            serviceBoxInputCheckedEvh(checkbox, { app_type_code: code, app_id, app_password: '******' })
          } catch (error) {
            console.error(error)
          }
        }
      }, 500)
    }
  }

  /**
   * 은행목록 셀렉터 확인
   * @param {*} item
   * @param {*} SetManagerName
   */
  const getBankCode = useCallback((item, SetManagerName) => {
    if (isSpgeCF) return
    const data = { parent_code: 'bank_code' }
    commonConfig
      .getCodeList(data)
      .then(result => {
        setSelectList({
          bank_code: [...selectList.bank_code, ...selectItemFormat(result.data, 'code_name', 'code')]
        })
      })
      .catch(error => {
        console.log(error)
      })
  }, [])

  //캔버스 서명 글씨
  const [differentCustomerIsSigned, setDifferentCustomerIsSigned] = useState(false)
  const [differentCustomerHelper, setDifferentCustomerHelper] = useState('')
  const [differentAccountIsSigned, setDifferentAccountIsSigned] = useState(false)
  const [differentAccountHelper, setDifferentAccountHelper] = useState('')

  //신청서 유효성 검사
  // try {
  //   if (errors && Object.keys(errors).length > 0) {
  //     console.log('errors', errors)
  //   }
  // } catch (e) {}

  /** 담당자코드 확인  */
  const getMangerVerf = useCallback((_, setManagerName) => {
    const managerCode = param.manager_code
    const data = {
      manager_code: managerCode || ''
    }

    let p
    if (isSpgeF) {
      p = commonConfig.spgeFMangerVerify(data)
    } else if (isSpgeCF) {
      if ('fill' === managerCode) {
        console.log('no manager code')
        return
      }
      p = commonConfig.spgeCfMangerVerify(data)
    } else {
      p = commonConfig.getMangerVerf(data)
    }

    p.then(result => {
      setManagerName(result.manager_name)
      setManagerIdx(result.manager_idx ?? '')
      setFranchiseManagerIdx(result?.payload?.franchise_manager_idx)

      if (result.result_code !== '00000') {
        alert('유효한 매니저코드가 아닙니다.')
        navi('/')
      }
    }).catch(error => {
      console.log(error)
    })
  }, [])

  /** 신청서 저장  */
  const setApply = useCallback(data => {
    applyConfig
      .setApply(data)
      .then(result => {
        setCost(result.service_amount)
        setWithdrawalDt(result.service_start_dt)
        setServiceStartDt(result.service_start_dt)
        setSuccessPopupState(true)
      })
      .catch(error => {
        if (error.code === 'E03010' && error.message.includes('Id')) {
          setErrorPopupMessage('사용중인 ID입니다')
        } else if (error.code === 'E00003' && error.message.includes('Password')) {
          setErrorPopupMessage('비밀번호 형식이 올바르지 않습니다')
        } else {
          setErrorPopupMessage(error.message)
        }
        setErrorPopupState(true)
        setBtnState(false)
        console.log(error)
      })
      .finally(setSubmitCount(0))
  }, [])

  /**
   *
   */
  const setApplyFranchise = useCallback(data => {
    applyConfig
      .applyFranchiseRegi(data)
      .then(result => {
        console.log(result)
        setSuccessPopupState(true)
      })
      .catch(error => {
        setBtnState(false)
        console.log(error)
      })
  }, [])

  const [bizNoTrue, setBizNoTrue] = useState(false)
  const [freeMonthYn, setFreeMonthYn] = useState(true) //날짜선택부분 토글
  const [bizNoHelper, setBizNoHelper] = useState()

  const [bizNo, setBizNo] = useState()

  const [isBiznoValid, setIsBiznoValid] = useState()

  const stateInit = () => {
    const bizNoInput = document.getElementById('biz_no').value
    setBizNo(bizNoInput)
    setFreeMonthYn(true)

    // 신청자와 예금주가 다른 경우 체크 헤제
    const account_check = document.getElementById('account_check')
    if (account_check && account_check.classList.contains('isOpen')) {
      account_check.classList.remove('isOpen')
    }

    different_customer_sign_ref?.current?.clear?.()
    setDifferentCustomerIsSigned(false)
    different_account_sign_ref?.current?.clear?.()
    setDifferentAccountIsSigned(false)

    const el = document.getElementsByClassName('service_wrap')[0]
    el &&
      el.childNodes.forEach((item, i) => {
        if (item.classList.contains('isOpen')) {
          item.classList.remove('isOpen')
          const check = item.firstChild.childNodes
          const input = item.lastChild.childNodes
          check.forEach(item => {
            if (item.tagName === 'INPUT') {
              item.checked = false
            }
          })
          input.forEach(item => {
            if (item.tagName === 'INPUT') {
              item.value = ''
            }
          })
        }
      })

    reset({
      shop_name: '',
      owner_name: '',
      owner_phone_no: '',
      account_name: '',
      account_phone_no: '',
      bank_code: '',
      account_no: '',
      account_jumin_biz_no: '',
      different_customer_name: '',
      apply_phone_no: ''
    })
    setValue('different_apply_customer', false)
    setValue('different_account', false)

    setValue('agree_yn', false)
    if (isSpgeCF) {
      setValue('agree_yn2', false)
      setValue('agree_yn3', false)
      // setValue('otp', '')
    }
    setValue('account_relation', '')
  }

  /**
   *
   */
  const onSuccessApplyBizNoValid = useCallback(result => {
    // utils function
    const setAccountIdAndPassword = id => {
      setShopId(id)
      setShopPassword(samplePassword)
      setShopConfirmPassword(samplePassword)
      setValue('id', id)
      setValue('password', samplePassword)
      setValue('confirmPassword', samplePassword)
    }

    setBizNoHelper('')
    setBizNoTrue(true)
    setIsBiznoValid('서비스 신청이 가능한 사업자 등록번호입니다.')
    if (isSpgeCF) {
      const tmp = result?.payload?.franchiseManager?.franchise_name
      if (tmp) {
        setPlatformName(tmp)
      }

      if (result?.payload?.responseType === 'ABLE_TO_MOVE_SHOP_FROM_CR_TO_CF') {
        setNotEditInfo(true)
        setIsSpge2Frch(true)
        handleToMoveFromSpongeToFranchise(result.payload['shop'])
        return
      }

      if (result?.payload?.responseType === 'SHOP_CF_EXISTED_IN_ANOTHER_BRAND') {
        setNotEditInfo(true)
        setCfExists(true)
        handleFranchiseShopExistingInAnotherBrand(result.payload['shop'])
        return
      }
      if (result?.payload?.responseType === 'REGISTERED_AND_PENDING') {
        handleFranchiseShopPending(result.payload['franchiseShopPending'])
        return
      }

      // NOT_REGISTERED_YET
    }

    if (isSpgeF) {
      if (result?.payload?.biz_no) {
        console.warn(result?.payload)
        const {
          franchise_idx,
          franchise_name,
          biz_no,
          representative_name,
          contact_person_mobile,
          contact_email,
          apply_image_url,
          business_registration_certificates,
          ready_dttm,
          usage_yn,
          manager_idx,
          blind_yn,
          reg_id,
          reg_dttm,
          mod_id,
          mod_dttm,
          name,
          manager_id
        } = result?.payload
        setValue('franchisee_name', franchise_name)
        setValueOfOwnerName(representative_name)
        setValue('owner_name', representative_name)
        setValue('contact_manager_name', name)
        setValueOfOwnerPhoneNo(contact_person_mobile)
        setValue('owner_phone_no', contact_person_mobile)
        setValue('contact_manager_email', contact_email)

        setAccountIdAndPassword(manager_id)
      }
      return
    }
    if (result.ceo_idx !== undefined) {
      setReSignup(true)
      setCeoIdx(result.ceo_idx)
      setAccountIdAndPassword(result.id)
    }
  }, [])

  /**
   *
   */
  const handleToMoveFromSpongeToFranchise = useCallback(result => {
    setBizNoHelper('')
    setBizNoTrue(true)
    setIsBiznoValid('스폰지R 서비스를 이용중인 가맹점입니다. 전환하시면 프랜차이즈 정보 제공 동의서가 발송 됍니다.')

    const { shop_name, biz_no_type, owner_phone_no, owner_name, shop_idx } = result

    setValue('shop_name', shop_name)
    setValue('shop_idx', shop_idx)
    setValueOfOwnerName(owner_name)
    setValue('owner_name', owner_name)
    setValueOfOwnerPhoneNo(owner_phone_no)
    setValue('owner_phone_no', owner_phone_no)
    setValue('biz_no_type', biz_no_type === 'c' ? 'c' : 'p')
    setShopPassword(samplePassword)
    setShopConfirmPassword(samplePassword)
    setValue('password', samplePassword)
    setValue('confirmPassword', samplePassword)

    setTimeout(() => {
      setValue('id', result.id)
    }, 100)
  }, [])

  /**
   *
   */
  const handleFranchiseShopExistingInAnotherBrand = useCallback(result => {
    const { shop_idx, shop_name, owner_name, owner_phone_no, biz_no_type } = result

    setBizNoHelper('')
    setBizNoTrue(true)
    setIsBiznoValid('서비스 신청이 가능한 사업자 등록번호입니다.')
    setValue('shop_name', shop_name)
    setValue('shop_idx', shop_idx)
    setValueOfOwnerName(owner_name)
    setValue('owner_name', owner_name)
    setValueOfOwnerPhoneNo(owner_phone_no)
    setValue('owner_phone_no', owner_phone_no)
    setValue('biz_no_type', biz_no_type === 'c' ? 'c' : 'p')
    setShopPassword(samplePassword)
    setShopConfirmPassword(samplePassword)
    setValue('password', samplePassword)
    setValue('confirmPassword', samplePassword)

    setTimeout(() => {
      setValue('id', result.id)
    }, 100)
  }, [])

  /**
   *
   */
  const handleFranchiseShopPending = useCallback(result => {
    const { shop_name, biz_no_type, phone_no, representative_name } = result

    setValue('shop_name', shop_name)
    setValueOfOwnerName(representative_name)
    setValue('owner_name', representative_name)
    setValueOfOwnerPhoneNo(phone_no)
    setValue('owner_phone_no', phone_no)
    setValue('biz_no_type', biz_no_type === 'c' ? 'c' : 'p')

    handleDeliveryAppPending(result.delivery_app_list || [])
  }, [])

  /**
   *
   */
  const handleDeliveryAppPending = useCallback(delivery_apps => {
    if (delivery_apps?.length) {
      setTimeout(function () {
        for (const app of delivery_apps) {
          try {
            const { app_type_code: code, app_id, app_password } = app
            const box = document.querySelector('#sevice-box-' + code)
            if (!box) continue
            const checkbox = box.querySelector("input[type='checkbox']")
            checkbox.checked = true
            box.querySelector('#service_id_' + code).value = app_id
            box.querySelector('#service_password_' + code).value = app_password
            serviceBoxInputCheckedEvh(checkbox, { app_type_code: code, app_id, app_password: app_password })
          } catch (error) {
            console.error(error)
          }
        }
      }, 500)
    }
  }, [])

  /**
   * 사업자번호 검증
   */
  const getApplyBizNoValid = useCallback(() => {
    console.log('getApplyBizNoValid')
    const managerCode = param.manager_code
    const bizNo = window.document.getElementById('biz_no').value.replace(/-/g, '')
    setNotEditInfo(false)
    setIsSpge2Frch(false)
    stateInit()

    // 사업자번호 체크 (사업자번호, 예금주 사업자번호)
    if (checkCorporateRegiNumber(bizNo) === false) {
      console.log('NOT VALID BY ALGORITHM')
      setBizNoHelper('사업자번호를 검증해주세요')
      return
    }

    let endpoint = ''
    if (bizNo.length === 10 || bizNo.length === 14) {
      const data = {
        biz_no: bizNo,
        manager_code: managerCode,
        bizTrans: new URLSearchParams(window.location.search).get('bizTrans')
      }
      if (isSpgeCF) {
        data['spge_c_f_yn'] = 'Y'
        endpoint = '/api/v1/apply/spge-cf/applyBizNoValid'
      }
      if (isSpgeF) {
        data['spge_f_yn'] = 'Y'
        endpoint = '/api/v1/apply/spge-f/applyBizNoValid'
      }

      applyConfig
        .applyBizNoValid(data, endpoint)
        .then(result => {
          if (result.result_code === '00000') {
            onSuccessApplyBizNoValid(result)
          } else {
            if (isSpgeCF) {
              if (result.result_code === 'E02004') {
                setBizNoHelper('초대받은 사업자 등록번호가 아닙니다. 담당자에게 문의해주세요.')
              } else if (result.result_code === 'E02008') {
                setBizNoHelper('서비스 이용중인 가맹점의 사업자번호입니다.')
              }
            } else if (isSpgeF) {
              if (result.result_code === 'E02002') {
                setBizNoHelper('이미 등록된 사업자번호입니다.')
              }
            } else {
              setBizNoHelper('사업자번호를 확인해 주세요.')
            }

            setBizNoTrue(false)
          }

          if (isSpgeCF || result.free_month_yn === 'N') {
            let startData = dayjs(result.service_start_dt).format('YYYY.MM.DD')
            let paymentDt = dayjs(result.payment_dt).add(2, 'month').format('YYYY.MM.01')
            // let withdrawalData = dayjs(result.service_start_dt).add(1, 'month').format('YYYY.MM.25')
            setFreeMonthYn(false)
            setServiceStartDt(startData)
            setWithdrawalDt(paymentDt)
          } else {
            let startData = dayjs().add(1, 'day').format('YYYY.MM.DD')
            let withdrawalData = dayjs().add(1, 'day').add(2, 'month').format('YYYY.MM.01')
            setServiceStartDt(startData)
            setWithdrawalDt(withdrawalData)
          }
        })
        .catch(error => {
          console.log(error)
        })
    }
  }, [])

  const [bizNoType, setBizNoType] = useState('p')

  const onBizNoType = e => {
    setBizNoType(e.target.value)
    setValue('biz_no_type', e.target.value)
    setValue('account_jumin_biz_no', '')
    errors.account_jumin_biz_no = ''
  }

  const onChangeBizNo = e => {
    let text = e.target.value
    if (text.length > 10 && text.indexOf('-') < 0) {
      text = text.slice(0, 10) + '-' + text.slice(10)
    }
    text = text
      .replace(/[^0-9.-]/g, '')
      .replace(/(\..*?)\..*/g, '$1')
      .replace(/^0[^.]/, '0')
    setValue('biz_no', text)
    setBizNoTrue(false)
    setIsBiznoValid('')
  }

  /**
   *
   * @param {*} item
   */
  const onSubmit = async item => {
    setBtnState(true)

    // return console.log(item)

    if (cfExists || isSpge2Frch) {
      const { shop_idx } = item ?? {}
      const managerCode = param.manager_code

      const data = {
        shop_idx,
        manager_code: managerCode || '',
        franchise_manager_idx: franchiseManagerIdx,
        bizTrans: new URLSearchParams(window.location.search).get('bizTrans'),
        biz_no: bizNo
      }

      if (!shop_idx) {
        alert('Some thing went wrong: ' + JSON.stringify(data))
        setBtnState(false)
        return
      }

      applyConfig
        .apiSendShopCfConfirmViaKakao(data)
        .then(result => {
          // console.log(result)
          const alimTalkResponse = result.alimTalkResponse
          if (alimTalkResponse && !alimTalkResponse.success) {
            alert('NHN send fail: ' + (alimTalkResponse.errorPhones ?? []).toString())
            return
          }
          setSuccessPopupState(true)
        })
        .catch(error => {
          // console.log(error)
          // setErrorPopupState(true)
          alert(error?.message)
        })
      return
    }

    if (bizNoTrue === false) {
      setBizNoHelper('사업자번호를 검증해주세요')
      return
    }

    // 사업자번호 체크 (사업자번호, 예금주 사업자번호)
    if (checkCorporateRegiNumber(item?.biz_no) === false) {
      setBtnState(false)
      return alert('사업자등록번호를 확인해주세요')
    }

    if (!isServiceManager && item?.account_jumin_biz_no.length === 10 && checkCorporateRegiNumber(item?.biz_no) === false) {
      setBtnState(false)
      return alert('예금주 사업자번호를 확인해주세요')
    }

    // Check confirm password
    if (item?.password !== item?.confirmPassword) {
      setBtnState(false)
      return alert('비밀번호와 비밀번호 확인이 일치하지 않습니다.')
    }

    if (!isSpgeF && param.shop_idx === undefined) {
      let noneAccountInfo
      if (serviceInfo?.length > 0) {
        serviceInfo.map((item, i) => {
          // console.log(item)
          if (item.app_id === '' || item.app_password === '') {
            let location = document.querySelector('.service_wrap').offsetTop
            window.scrollTo({ top: location - 100, behavior: 'smooth' })
            setServiceHelper('계정정보를 확인해주세요.')
            noneAccountInfo = true
            setBtnState(false)
          }
        })
      } else {
        let location = document.querySelector('.service_wrap').offsetTop
        window.scrollTo({ top: location - 100, behavior: 'smooth' })
        setServiceHelper('1개 이상의 서비스 신청하기 버튼을 선택해주세요.')
        setBtnState(false)
        return
      }

      if (noneAccountInfo) return
    }

    // 신청자와 대표자가 다르다에 체크했는데 서명안했을때
    const diff_apply_value_check = getValues('different_apply_customer')

    if (!differentCustomerIsSigned) {
      let location = (document.querySelector('#apply_check') || document.querySelector('.terms_sign')).offsetTop
      window.scrollTo({ top: location - 100, behavior: 'smooth' })
      setDifferentCustomerHelper('서명을 해주세요.')
      setBtnState(false)
      return
    }

    // 예금주가 다르다에 체크했는데 서명안했을때
    const diff_account_value_check = getValues('different_account')
    if (diff_account_value_check && !differentAccountIsSigned) {
      let location = document.querySelector('#account_check').offsetTop
      window.scrollTo({ top: location - 100, behavior: 'smooth' })
      setDifferentAccountHelper('서명을 해주세요.')
      setBtnState(false)
      return
    }

    let apply_name
    if (item?.different_apply_customer) {
      apply_name = item?.different_customer_name
    } else {
      apply_name = item?.apply_name
    }

    try {
      const el = document.querySelector('.application_sheet')
      const canvas = await html2canvas(el, {
        scale: 2,
        logging: true,
        letterRendering: 1,
        allowTaint: true,
        useCORS: true,
        imageTimeout: 20000,

        onclone: document => {
          document.querySelector('.header').style.display = 'none'
        }
      })

      let bank_neme = selectList.bank_code.filter(data => data.value === item?.bank_code)
      const data = {
        manager_code: param?.manager_code || '', //담당자코드
        biz_no: item?.biz_no.replace(/-/g, '') || '', //사업자등록번호
        shop_name: item?.shop_name || '', //사업자명
        owner_name: item?.owner_name || '', //대표자명
        owner_phone_no: item?.owner_phone_no || '', //대표자휴대폰
        bank_code: item?.bank_code || '', //은행코드
        bank_name: bank_neme[0]?.name || '', //은행이름
        account_name: item?.account_name || '', //예금주명
        account_phone_no: item?.account_phone_no || '', //예금주 휴대폰

        id: item?.id || '',
        password: item?.password || '',

        account_no: item?.account_no || '', //계좌번호
        service_start_dt: (serviceStartDt ?? '').replaceAll('.', '') || '', //서비스 시작예정일
        service_amount: cost || '', //서비스이용금액(부가세별도)
        delivery_app_list: serviceInfo, //서비스 신청 목록 + 아이디 비번
        account_jumin_biz_no: item?.account_jumin_biz_no || '', //예금주 생년월일/사업자번호
        agree_yn: item?.agree_yn === true ? 'Y' : 'N' || '', //약관동의여부
        apply_name: apply_name || '', //신청자명
        apply_phone_no: item?.apply_phone_no || '', //신청자 휴대폰
        account_relation: item?.account_relation || '', //예금주와의 관계
        biz_no_type: item?.biz_no_type || 'p',
        apply_file: canvas.toDataURL('image/jpeg') || ''
      }
      //insert shop_idx in case normal shop update to premium shop
      if (param.shop_idx) {
        data.shop_idx = param.shop_idx
        data.service_amount = '0'
      }
      //add ceo idx in case re signup
      if (reSignup) {
        data.ceo_idx = ceoIdx
      }

      // if (submitCount !== 0) return console.log('not 0')
      setSubmitCount(submitCount + 1)

      if (isSpgeCF) {
        // * submit: apply spge CF
        data.agree_yn2 = item?.agree_yn2 ? 'Y' : 'N'
        data.agree_yn3 = item?.agree_yn3 ? 'Y' : 'N'
        data['spge_c_f_yn'] = 'Y'
        // data.otp = item?.otp
        data['service_start_dt'] = dayjs().add(1, 'day').format('YYYYMMDD')
        // console.log('data', data)
      }

      if (isSpgeF) {
        // * submit: apply franchise
        const upload_files = item?.upload_file
        for (let i = 0; i < upload_files.length; i++) {
          data[`upload_file${i + 1}`] = await file2DataUrl(upload_files[i])
        }
        data['upload_file_count'] = upload_files.length
        data['contact_manager_name'] = item?.contact_manager_name
        data['manager_name'] = item?.contact_manager_name
        data['franchisee_name'] = item?.franchisee_name
        data['contact_manager_email'] = item?.contact_manager_email
        data['manager_idx'] = managerIdx

        setApplyFranchise(data)
        return
      }
      // * submit: apply spge CR or normal2premium
      if (isNormal2Premium) {
        if (data['delivery_app_list'] && data['delivery_app_list'].length > 0) {
          console.warn('[DEV] Not allow edit delivery_app_list')
        }
        // not allow edit
        data['delivery_app_list'] = []
      }

      //
      if (isServiceManager) {
        data.unlimited_yn = 'Y'
        data.service_amount = 0
      }

      setApply(data)
    } catch (error) {
      alert(error)
    }
  }

  const [platformName, setPlatformName] = useState('')
  const [ownerName, setOwnerName] = useState()
  const [ownerPhoneNo, setOwnerPhoneNoName] = useState()
  const [accountName, setAccountName] = useState()

  const setValueOfOwnerPhoneNo = v => {
    setValue('apply_phone_no', v)
    setOwnerPhoneNoName(v)
  }
  const setValueOfOwnerName = v => {
    setValue('different_customer_name', v)
    setOwnerName(v)
  }

  const ownerNameSetting = e => {
    setValueOfOwnerName(e.target.value)
  }
  const ownerPhoneNoSetting = e => {
    setValueOfOwnerPhoneNo(e.target.value)
  }

  const accountNameSetting = e => {
    setValue('apply_name', e.target.value)
    setAccountName(e.target.value)
  }

  const idSetting = e => {
    setValue('id', e.target.value)
    setShopId(e.target.value)
  }

  const passwordSetting = e => {
    setValue('password', e.target.value)
    setShopPassword(e.target.value)
  }

  const confirmPassword = e => {
    setValue('confirmPassword', e.target.value)
    setShopConfirmPassword(e.target.value)
  }

  //체크박스 체크 여부
  const [diffApplyCustomerCheck, setDiffApplyCustomerCheck] = useState(true)
  const differentApplyCustomerCheck = e => {
    setDiffApplyCustomerCheck(!e.target.checked)
    if (e.target.checked) {
      setValue('different_customer_name', '')
      setValue('apply_phone_no', '')
    } else {
      setValue('different_customer_name', ownerName)
      setValue('apply_phone_no', ownerPhoneNo)
    }
  }
  const [diffAccountCheck, setDiffAccountCheck] = useState(true)
  const differentAccountCheck = e => {
    if (e.target.checked === true) {
      e.target.parentNode.parentNode.classList.add('isOpen')
    } else {
      e.target.parentNode.parentNode.classList.remove('isOpen')
    }
    setDiffAccountCheck(!e.target.checked)
  }

  //약관 팝업 열기
  function termsPopupOpen() {
    document.querySelector('.terms_popup:not(.forCF)').classList.add('open')
    document.querySelector('.TermsPopup:not(.forCF)').classList.add('open')
    document.querySelector('html').style.overflow = 'hidden'
  }

  function termsPopupOpen4CF() {
    document.querySelector('.terms_popup.forCF').classList.add('open')
    document.querySelector('.TermsPopup.forCF').classList.add('open')
    document.querySelector('html').style.overflow = 'hidden'
  }

  //약관 팝업 닫기
  function termsPopupClose(e) {
    if (e.target.className === 'btn_default') {
      //동의 하기 버튼 누른 경우
      setValue('agree_yn', true)
    }
    document.querySelector('.terms_popup:not(.forCF)').classList.remove('open')
    document.querySelector('.TermsPopup:not(.forCF)').classList.remove('open')
    document.querySelector('html').style.overflow = 'auto'
  }

  function termsPopupClose4CF(e) {
    if (e.target.className === 'btn_default') {
      //동의 하기 버튼 누른 경우
      setValue('agree_yn3', true)
    }
    document.querySelector('.terms_popup.forCF').classList.remove('open')
    document.querySelector('.TermsPopup.forCF').classList.remove('open')
    document.querySelector('html').style.overflow = 'auto'
  }

  return {
    location,
    param,
    formRef,
    navi,
    different_customer_sign_ref,
    different_account_sign_ref,
    managerName,
    setManagerName,
    serviceNum,
    setServiceNum,
    serviceInfo,
    setServiceInfo,
    serviceBoxInputEvh,
    serviceHelper,
    setServiceHelper,
    cost,
    setCost,
    successPopupState,
    setSuccessPopupState,
    errorPopupState,
    setErrorPopupState,
    errorPopupMessage,
    setErrorPopupMessage,
    btnState,
    setBtnState,
    serviceStartDt,
    setServiceStartDt,
    withdrawalDt,
    setWithdrawalDt,
    submitCount,
    setSubmitCount,
    shopId,
    setShopId,
    shopPassword,
    setShopPassword,
    shopConfirmPassword,
    setShopConfirmPassword,
    reSignup,
    setReSignup,
    ceoIdx,
    setCeoIdx,
    samplePassword,
    selectList,
    setSelectList,
    getShopInfo,
    getBankCode,
    differentCustomerIsSigned,
    setDifferentCustomerIsSigned,
    differentCustomerHelper,
    setDifferentCustomerHelper,
    differentAccountIsSigned,
    setDifferentAccountIsSigned,
    differentAccountHelper,
    setDifferentAccountHelper,
    formSchema,
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    getMangerVerf,
    setApply,
    bizNoTrue,
    setBizNoTrue,
    freeMonthYn,
    setFreeMonthYn,
    bizNoHelper,
    setBizNoHelper,
    bizNo,
    setBizNo,
    isBiznoValid,
    setIsBiznoValid,
    stateInit,
    getApplyBizNoValid,
    bizNoType,
    setBizNoType,
    onBizNoType,
    onChangeBizNo,
    onSubmit,
    ownerName,
    setOwnerName,
    ownerPhoneNo,
    setOwnerPhoneNoName,
    accountName,
    setAccountName,
    ownerNameSetting,
    ownerPhoneNoSetting,
    accountNameSetting,
    idSetting,
    passwordSetting,
    confirmPassword,
    diffApplyCustomerCheck,
    setDiffApplyCustomerCheck,
    differentApplyCustomerCheck,
    diffAccountCheck,
    setDiffAccountCheck,
    differentAccountCheck,
    termsPopupOpen4CF,
    termsPopupOpen,
    termsPopupClose,
    termsPopupClose4CF,
    errors,
    isSpgeCF: isSpgeCF ?? false,
    isSpgeF: isSpgeF ?? false,
    isNormal2Premium: param.shop_idx !== undefined,
    setError,
    clearErrors,
    setValueOfOwnerName,
    setValueOfOwnerPhoneNo,
    notEditInfo,
    isSpge2Frch,
    cfExists,
    platformName,
    isTargetManager,
    isServiceManager
  }
}
export default useApplicationForm

const selectItemFormat = (data, name, value) => {
  return data.map(item => {
    return {
      name: item[name]?.replaceAll('\r', '')?.replaceAll('\n', ''),
      value: typeof item[value] === 'string' ? item[value]?.replaceAll('\r', '')?.replaceAll('\n', '') : item[value]
    }
  })
}
